import React, { useCallback, useContext, useEffect, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { toast, ToastContainer } from 'react-toastify';
import qs from 'qs';

import Swal from 'sweetalert2';
import { Button } from '../../../../../components/Button';
import LoadingCondomob from '../../../../../components/LoadingCondomob';

import { AppCommonContext } from '../../../../../contexts/AppCommonContext';

import protocolVoteBkg from '../../../../../assets/protocolVoteBkg.svg';

import api from '../../../../../services/api';

import {
  Container,
  LineRow,
  TitleContainer,
  TitleBackButton,
  Title,
  CardContainer,
  CardContent,
  CardContentPollDate,
  CardContentPollInfoContainer,
  CardContentPollInfoQuestion,
  CardContentPollInfoOptions,
  CardContentPollMessage,
  CardContentPollMessageButton,
  CardContentPollNotVoted,
  CardContentPollInfoPercentage,
  CardContentPollInfoTitle,
  CardContentPollVotedProtocol,
  CardContentPollVotedProtocolData,
  CardContentPollVotedProtocolLineLabel,
  CardContentPollVotedProtocolLineinfo,
  CardFooter,
  CardContentPollCanceVoteButton,
} from './styles';

type VAVoteInfoAssemblyBlockProps = {
  descricao: string;
  fileGcs: boolean;
  fileName?: string;
  html: string;
  id: number;
  idAviso: number;
  link: string;
  ordem: number;
  tipo: {
    codigo: string;
    id: number;
    nome: string;
  };
};

type SurveyVoteDataOptionsProps = {
  id: number;
  opcao: string;
  percentual: number;
  votos: string;
};

type SurveyVoteProps = {
  dataVoto: number;
  dvcAddress: string;
  dvcManufacturer: string;
  dvcModel: string;
  dvcPlatform: string;
  dvcPlatformVersion: string;
  id: number;
  opcoes: Array<number>;
  userEmail: string;
};

type SurveyVoteDataProps = {
  blocos: Array<VAVoteInfoAssemblyBlockProps>;
  dataAtualizacao: number;
  dataEnquete: string;
  dataFim: string;
  dataInicio: string;
  id: number;
  maximoEscolhas: number;
  minimoEscolhas: number;
  opcoes: Array<SurveyVoteDataOptionsProps>;
  percentualVotos: number;
  pergunta: string;
  por: string;
  status: {
    cor: string;
    descricao: string;
    encerrada: boolean;
  };
  titulo: string;
  totalUnidades: number;
  totalVotos: number;
  unidadeVotou: boolean;
  voto: SurveyVoteProps;
  votoAberto: boolean;
  votoCancelavel: boolean;
  votoVinculado: boolean;
};

interface VAVoteInfoLocationState {
  pollId: number;
  assemblyId: number;
  assemblyEndTime: string;
  assemblyStartTime: string;
  mensagemPresente?: string; // Opcional, pois não usamos diretamente na lógica
  registraPresencaApp: boolean;
  unidadePresente: boolean; // Presença inicial vinda da navegação
  votantePresente: boolean;
  activeTab?: number; // Para navegação de volta
}

const VAVoteInfo: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [surveyVoteData, setSurveyVoteData] =
    useState<SurveyVoteDataProps | null>(null); // Iniciar como null

  const history = useHistory();
  const location = useLocation<VAVoteInfoLocationState>(); // Usar hook useLocation
  const { t } = useTranslation();
  const { assemblyUserPresence, setAssemblyUserPresence } =
    useContext(AppCommonContext);

  // Extraindo de location.state com tipagem
  const {
    pollId,
    assemblyId,
    assemblyEndTime,
    assemblyStartTime,
    registraPresencaApp,
    unidadePresente: initialUnidadePresente, // Renomeado para clareza
    votantePresente,
  } = location.state || ({} as VAVoteInfoLocationState); // Fallback para objeto vazio se state for undefined

  const vaVoteInfoHandleUnitAssemblyPresence = useCallback(async () => {
    const body = { assembleia: assemblyId };
    const url = `assembleia/presencaUnidade/save`;
    setLoading(true);
    try {
      await api.post(url, qs.stringify(body));
      setAssemblyUserPresence(true);
      toast.success(`Presença registrada com sucesso!!`, {
        closeOnClick: true,
        autoClose: 3000,
        hideProgressBar: false,
        draggable: true,
        pauseOnHover: true,
        progress: undefined,
        position: 'top-center',
      });
    } catch (err: any) {
      const errorMsg =
        err?.response?.data?.error ||
        `Ops! Algo não saiu como esperado ao registrar presença!`;
      Swal.fire({
        icon: `error`,
        text: errorMsg,
        title: t('common.words.oops'),
        backdrop: `rgba(0,0,0,0.9)`,
      });
    } finally {
      setLoading(false);
    }
  }, [assemblyId, setAssemblyUserPresence, t]);

  const getSurveyVoteData = useCallback(async () => {
    if (!pollId) {
      // console.warn("pollId não encontrado em location.state. Não é possível buscar dados da enquete.");
      setLoading(false);
      setSurveyVoteData(null); // Garante que não haja dados antigos
      // Opcionalmente, mostrar um erro ao usuário ou redirecionar
      Swal.fire({
        icon: `error`,
        text: 'Não foi possível carregar os detalhes da votação. Tente voltar e acessar novamente.',
        title: t('common.words.oops'),
        backdrop: `rgba(0,0,0,0.9)`,
      });
      return;
    }
    setLoading(true);
    const url = '/enquete/get';
    try {
      const res = await api.get(url, { params: { id: pollId } });
      setSurveyVoteData(res.data);
    } catch (err: any) {
      setSurveyVoteData(null);
      const errorMsg =
        err?.response?.data?.error ||
        `Ops! Algo não saiu como esperado ao buscar dados da enquete!`;
      Swal.fire({
        icon: `error`,
        text: errorMsg,
        title: t('common.words.oops'),
        backdrop: `rgba(0,0,0,0.9)`,
      });
    } finally {
      setLoading(false);
    }
  }, [pollId, t]);

  const handleUserCancelVote = useCallback(async () => {
    if (!surveyVoteData?.voto?.id) return;
    setLoading(true);
    const url = `/enquete/cancelarVoto`;
    try {
      await api.post(url, qs.stringify({ id: surveyVoteData.voto.id }));
      toast.success(`Seu voto foi cancelado com sucesso!`, {
        position: 'top-center',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      await getSurveyVoteData(); // Rebusca os dados para atualizar
    } catch (err: any) {
      const errorMsg =
        err?.response?.data?.error ||
        `Ops! Algo não saiu como esperado ao cancelar o voto!`;
      Swal.fire({
        icon: `error`,
        text: errorMsg,
        title: t('common.words.oops'),
        backdrop: `rgba(0,0,0,0.9)`,
      });
    } finally {
      setLoading(false);
    }
  }, [surveyVoteData, getSurveyVoteData, t]);

  useEffect(() => {
    // Sincroniza o estado de presença do contexto com o inicial da navegação, se aplicável.
    if (initialUnidadePresente && !assemblyUserPresence) {
      setAssemblyUserPresence(true);
    }
  }, [initialUnidadePresente, assemblyUserPresence, setAssemblyUserPresence]);

  useEffect(() => {
    getSurveyVoteData();
  }, [getSurveyVoteData]); // getSurveyVoteData agora é memorizado com useCallback

  // --- Cálculo de Estados Lógicos para Renderização ---
  const now = dayjs();
  // Validação e parse das datas. Assume-se que dayjs está configurado com 'pt-br' globalmente
  // ou que o formato de assemblyStartTime/EndTime não precisa de locale específico no parse.
  const startDateTime = assemblyStartTime
    ? dayjs(assemblyStartTime, 'DD/MM/YYYY HH:mm')
    : null;
  const endDateTime = assemblyEndTime
    ? dayjs(assemblyEndTime, 'DD/MM/YYYY HH:mm')
    : null;

  const isAssemblyActive =
    startDateTime &&
    endDateTime &&
    now.isAfter(startDateTime) &&
    now.isBefore(endDateTime);
  const userHasVoted = !!surveyVoteData?.unidadeVotou; // !! para garantir booleano

  // Regra Principal (Pode Votar?):
  // SE votantePresente: false => O usuário PODE VOTAR (independente de assemblyUserPresence).
  // SE votantePresente: true  => O usuário SÓ PODE VOTAR SE assemblyUserPresence: true.
  const canUserActuallyVote = !votantePresente || assemblyUserPresence;

  // Lógica para exibir o botão de registrar presença:
  const shouldShowRegisterPresenceButton =
    isAssemblyActive &&
    !userHasVoted &&
    votantePresente &&
    !assemblyUserPresence &&
    registraPresencaApp;

  // Mensagem quando presença é obrigatória, usuário não está presente E NÃO PODE registrar pelo app.
  const showPresenceRequiredButNotPossibleMessage =
    isAssemblyActive &&
    !userHasVoted &&
    votantePresente &&
    !assemblyUserPresence &&
    !registraPresencaApp;

  if (loading) {
    return <LoadingCondomob />;
  }

  if (!surveyVoteData || !pollId || !assemblyStartTime || !assemblyEndTime) {
    // Verifica se dados essenciais estão presentes
    // Esta mensagem pode ser mostrada se getSurveyVoteData falhar e setSurveyVoteData(null)
    // ou se os dados de location.state não vieram corretamente.
    return (
      <Container>
        <TitleContainer>
          <TitleBackButton onClick={() => history.goBack()}>
            <FontAwesomeIcon icon={['far', 'angle-left']} />
            {t('common.words.back')}
          </TitleBackButton>
          <Title>Assembleias Virtuais - Votação</Title>
        </TitleContainer>
        <CardContentPollMessage style={{ marginTop: '20px' }}>
          <p>
            Não foi possível carregar os detalhes da votação. Por favor, tente
            voltar e acessar novamente.
          </p>
        </CardContentPollMessage>
      </Container>
    );
  }

  // Função para formatar datas do card, evitando repetição
  const formatDateForDisplay = (
    dateString: string,
    format = 'DD/MM/YYYY HH:mm',
  ) => {
    const dateObj = dayjs(dateString, 'DD/MM/YYYY HH:mm'); // Assumindo formato de entrada
    return dateObj.isValid() ? dateObj.format(format) : dateString;
  };

  return (
    <>
      <TitleContainer>
        <TitleBackButton
          onClick={() => {
            history.push({
              pathname: '/vaTabs', // Idealmente, use constante de rota
              state: {
                assemblyId,
                assemblyEndTime,
                assemblyStartTime,
                registraPresencaApp,
                unidadePresente: assemblyUserPresence, // Passa o estado de presença atualizado
                votantePresente,
                activeTab: location.state?.activeTab || 2, // Usa o activeTab de location.state ou fallback
              } as VAVoteInfoLocationState,
            });
          }}
        >
          <FontAwesomeIcon icon={['far', 'angle-left']} />
          {t('common.words.back')}
        </TitleBackButton>
        <Title>Assembleias Virtuais - Votação</Title>
      </TitleContainer>
      <Container>
        <CardContainer>
          <CardContent>
            <strong>{surveyVoteData.titulo}</strong>
            <CardContentPollDate encerrada={surveyVoteData.status.encerrada}>
              <FontAwesomeIcon icon={['fas', 'calendar']} />
              {formatDateForDisplay(surveyVoteData.dataInicio)} a{' '}
              {formatDateForDisplay(surveyVoteData.dataFim)} -{' '}
              <span> {surveyVoteData.status.descricao}</span>
            </CardContentPollDate>
            <CardContentPollInfoContainer>
              <CardContentPollInfoQuestion
                dangerouslySetInnerHTML={{ __html: surveyVoteData.pergunta }}
              />
              <CardContentPollInfoOptions>
                {/* --- INÍCIO DA LÓGICA DE VOTAÇÃO REATORADA --- */}

                {!isAssemblyActive && (
                  <CardContentPollMessage>
                    <p>
                      {now.isBefore(startDateTime)
                        ? 'Por favor, aguarde o início da assembleia para poder votar.'
                        : 'A votação para esta assembleia já foi encerrada.'}
                    </p>
                  </CardContentPollMessage>
                )}

                {isAssemblyActive && userHasVoted && (
                  <>
                    <CardContentPollVotedProtocol imgBkg={protocolVoteBkg}>
                      <LineRow>
                        <CardContentPollVotedProtocolData>
                          <CardContentPollVotedProtocolLineLabel>
                            Protocolo do voto:
                          </CardContentPollVotedProtocolLineLabel>
                          <CardContentPollVotedProtocolLineinfo>
                            {surveyVoteData.voto.id}
                          </CardContentPollVotedProtocolLineinfo>
                        </CardContentPollVotedProtocolData>
                      </LineRow>
                      <LineRow>
                        <CardContentPollVotedProtocolData>
                          <CardContentPollVotedProtocolLineLabel>
                            Data:
                          </CardContentPollVotedProtocolLineLabel>
                          <CardContentPollVotedProtocolLineinfo>
                            {dayjs(surveyVoteData.voto.dataVoto).format(
                              'DD/MM/YYYY [às] HH:mm:ss',
                            )}
                          </CardContentPollVotedProtocolLineinfo>
                        </CardContentPollVotedProtocolData>
                      </LineRow>
                      <LineRow>
                        <CardContentPollVotedProtocolData>
                          <CardContentPollVotedProtocolLineLabel>
                            Dispositivo:
                          </CardContentPollVotedProtocolLineLabel>
                          <CardContentPollVotedProtocolLineinfo>
                            {surveyVoteData.voto.dvcPlatform}
                          </CardContentPollVotedProtocolLineinfo>
                        </CardContentPollVotedProtocolData>
                        <CardContentPollVotedProtocolData>
                          <CardContentPollVotedProtocolLineLabel>
                            Marca:
                          </CardContentPollVotedProtocolLineLabel>
                          <CardContentPollVotedProtocolLineinfo>
                            {surveyVoteData.voto.dvcPlatformVersion}
                          </CardContentPollVotedProtocolLineinfo>
                        </CardContentPollVotedProtocolData>
                      </LineRow>
                      <LineRow>
                        <CardContentPollVotedProtocolData>
                          <CardContentPollVotedProtocolLineinfo>
                            {surveyVoteData.voto.dvcAddress}
                          </CardContentPollVotedProtocolLineinfo>
                        </CardContentPollVotedProtocolData>
                      </LineRow>
                      <LineRow>
                        <CardContentPollVotedProtocolData>
                          <CardContentPollVotedProtocolLineLabel>
                            E-mail:
                          </CardContentPollVotedProtocolLineLabel>
                          <CardContentPollVotedProtocolLineinfo>
                            {surveyVoteData.voto.userEmail}
                          </CardContentPollVotedProtocolLineinfo>
                        </CardContentPollVotedProtocolData>
                      </LineRow>
                    </CardContentPollVotedProtocol>
                    <CardContentPollInfoTitle>
                      <FontAwesomeIcon icon={['fas', 'chart-pie']} />
                      <p>Resultados</p>
                    </CardContentPollInfoTitle>
                    <>
                      {surveyVoteData.opcoes.map(opt => (
                        <CardContentPollInfoPercentage
                          key={opt.id}
                          percentage={opt.percentual}
                          chosenOption={
                            surveyVoteData.voto?.opcoes?.some(
                              (votedOptId: number) => votedOptId === opt.id,
                            ) || false
                          }
                        >
                          <label htmlFor={opt.opcao}>{`${opt.opcao}`}</label>
                          <span id={opt.opcao}>
                            <div>
                              <text>{opt.percentual}%</text>
                            </div>
                          </span>
                          {surveyVoteData.voto?.opcoes?.some(
                            (votedOptId: number) => votedOptId === opt.id,
                          ) && (
                            <p style={{ fontStyle: 'italic' }}>
                              {t('polls.descriptionPoll.pollUserVotedDate')}
                              {dayjs(surveyVoteData.voto.dataVoto).format(
                                'DD/MM/YYYY HH:mm:ss',
                              )}
                            </p>
                          )}
                        </CardContentPollInfoPercentage>
                      ))}
                    </>
                    {surveyVoteData.votoCancelavel && (
                      <CardContentPollCanceVoteButton
                        onClick={handleUserCancelVote}
                      >
                        <p>CANCELAR VOTO</p>
                      </CardContentPollCanceVoteButton>
                    )}
                  </>
                )}

                {isAssemblyActive && !userHasVoted && (
                  <>
                    {shouldShowRegisterPresenceButton && (
                      <>
                        <CardContentPollMessage>
                          <p>
                            Para que você possa votar na assembleia é necessário
                            que confirme a sua presença.
                          </p>
                        </CardContentPollMessage>
                        <CardContentPollMessageButton
                          onClick={vaVoteInfoHandleUnitAssemblyPresence}
                        >
                          <FontAwesomeIcon
                            icon={['fas', 'hand-point-up']}
                            size="lg"
                          />
                          <p>Registrar presença</p>
                        </CardContentPollMessageButton>
                      </>
                    )}

                    {canUserActuallyVote && !shouldShowRegisterPresenceButton && (
                      <CardContentPollNotVoted>
                        <p>
                          Até o momento não temos o registro do seu voto. Deseja
                          votar nessa enquete?
                        </p>
                        <Button
                          icon="check"
                          iconFamily="fas"
                          model="default"
                          onClick={() => {
                            history.push({
                              pathname: `/vaVote`, // Idealmente, use constante
                              state: { pollId, assemblyId }, // Passar dados necessários para a tela de voto
                            });
                          }}
                          title={t('polls.voteInPoll.vote')}
                        />
                      </CardContentPollNotVoted>
                    )}

                    {showPresenceRequiredButNotPossibleMessage && (
                      <CardContentPollMessage>
                        <p>
                          Sua presença é necessária para votar nesta assembleia.
                          O registro de presença não está habilitado através do
                          aplicativo no momento. Por favor, verifique outros
                          meios para registrar sua presença ou entre em contato
                          com a administração.
                        </p>
                      </CardContentPollMessage>
                    )}

                    {/* Fallback para caso o usuário não possa votar e nenhuma das mensagens acima se aplique.
                        Ex: `votantePresente` é true, `assemblyUserPresence` é false, `registraPresencaApp` é false (já coberto)
                        Este é um "catch-all" que idealmente não seria atingido se as lógicas acima forem completas.
                    */}
                    {!canUserActuallyVote &&
                      !shouldShowRegisterPresenceButton &&
                      !showPresenceRequiredButNotPossibleMessage && (
                        <CardContentPollMessage>
                          <p>
                            Você não pode votar no momento. Verifique as
                            condições ou se sua presença foi registrada.
                          </p>
                        </CardContentPollMessage>
                      )}
                  </>
                )}
                {/* --- FIM DA LÓGICA DE VOTAÇÃO REATORADA --- */}
              </CardContentPollInfoOptions>
            </CardContentPollInfoContainer>
          </CardContent>
          <CardFooter>
            <LineRow style={{ marginBottom: '0.4rem' }}>
              <FontAwesomeIcon icon={['fas', 'user']} />
              {surveyVoteData.por}
            </LineRow>
            <LineRow>
              <FontAwesomeIcon icon={['fas', 'clock']} />
              {formatDateForDisplay(
                surveyVoteData.dataEnquete,
                'DD/MM/YYYY HH:mm:ss',
              )}
            </LineRow>
          </CardFooter>
        </CardContainer>
        <ToastContainer position="top-center" /* ... */ />
      </Container>
    </>
  );
};

export default VAVoteInfo;
